import React, { useState, useEffect } from 'react';
import { Paper, Title, Group, Button, Stack, Text, SegmentedControl } from '@mantine/core';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { IconRefresh } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

export default function SystemResourceChart({ user, apiBaseUrl }) {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [timeRange, setTimeRange] = useState('24h');

  const fetchData = () => {
    if (!user || !user.email) return;
    setLoading(true);
    
    const end = new Date();
    let start = new Date();
    switch (timeRange) {
      case '1h': start = new Date(end.getTime() - (1 * 60 * 60 * 1000)); break;
      case '24h': start = new Date(end.getTime() - (24 * 60 * 60 * 1000)); break;
      case '7d': start = new Date(end.getTime() - (7 * 24 * 60 * 60 * 1000)); break;
      case '30d': start = new Date(end.getTime() - (30 * 24 * 60 * 60 * 1000)); break;
      case '3m': start = new Date(end.getTime() - (90 * 24 * 60 * 60 * 1000)); break;
      case '6m': start = new Date(end.getTime() - (180 * 24 * 60 * 60 * 1000)); break;
      default: start = new Date(end.getTime() - (24 * 60 * 60 * 1000));
    }
    const isoStart = start.toISOString();
    const isoEnd = end.toISOString();

    fetch(`${apiBaseUrl}/api/reports/system-resources?ownerEmail=${encodeURIComponent(user.email)}&startDate=${isoStart}&endDate=${isoEnd}`)
      .then(res => {
        if(!res.ok) throw new Error('Network response was not ok');
        return res.json();
      })
      .then(json => {
        const result = json.map(item => {
           const time = new Date(item.timestamp);
           return {
             time: time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
             cpu: Math.round(item.cpuUsagePercent * 10) / 10,
             ramMB: Math.round(item.usedRamBytes / 1048576),
             totalRamMB: Math.round(item.totalRamBytes / 1048576)
           };
        });
        // Downsample if too many points
        let sampled = result;
        if (result.length > 200) {
            const step = Math.ceil(result.length / 200);
            sampled = result.filter((_, i) => i % step === 0);
        }
        setData(sampled);
        setLoading(false);
      })
      .catch(err => {
        console.error("Error fetching system metrics:", err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, [user, timeRange]);

  return (
    <Paper p="xl" radius="md" withBorder mt="xl" bg="white">
      <Group justify="space-between" mb="md">
        <Stack gap={0}>
          <Title order={3} c="gray.8">System Resources & Usage</Title>
          <Text size="sm" c="dimmed">CPU, RAM and Thread usage on the VPS Node</Text>
        </Stack>
        <Group>
          <SegmentedControl 
            size="xs"
            value={timeRange}
            onChange={setTimeRange}
            data={[
              { label: t('time_1h', '1h'), value: '1h' },
              { label: t('time_24h', '24h'), value: '24h' },
              { label: t('time_7d', '7 Dni'), value: '7d' },
              { label: t('time_30d', '30 Dni'), value: '30d' },
              { label: t('time_3m', '3 Mies.'), value: '3m' },
              { label: t('time_6m', '6 Mies.'), value: '6m' }
            ]}
          />
          <Button 
            variant="light" 
            leftSection={<IconRefresh size={16} />} 
            onClick={fetchData} 
            loading={loading}
          >
            Odśwież
          </Button>
        </Group>
      </Group>

      {data.length === 0 ? (
        <Text c="dimmed">Brak danych (uruchomiono przed chwilą lub brak uprawnień).</Text>
      ) : (
        <Stack gap="xl">
          <div style={{ height: 300, width: '100%' }}>
            <Title order={5} mb="sm" c="dimmed">Użycie CPU (%)</Title>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="cpu" name="CPU (%)" stroke="#fa5252" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div style={{ height: 300, width: '100%' }}>
            <Title order={5} mb="sm" c="dimmed">Zużycie pamięci RAM (MB)</Title>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="ramMB" name="RAM Używany (MB)" stroke="#228be6" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="totalRamMB" name="RAM Całkowity (MB)" stroke="#868e96" strokeWidth={2} strokeDasharray="5 5" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Stack>
      )}
    </Paper>
  );
}
